import type {
  IFSCCode,
  PinCode,
  CricketData,
  Festival,
  RTOCode,
  HTMLData,
  RailwayStation,
  STDCode,
  USSDCode
} from '../types/data';

// Cache for loaded data
const dataCache = new Map<string, any>();

// Base URL for data files (supports local dev and CloudFront/S3 via env)
const BASE_URL = (import.meta as any).env?.VITE_DATA_BASE_URL as string | undefined;

const joinUrl = (base: string, path: string): string => {
  if (!base) return path;
  const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
};

const getDataUrl = (filename: string): string => {
  // If BASE_URL is provided, fetch from CDN; otherwise, use local public path
  if (BASE_URL) {
    return joinUrl(BASE_URL, `data/${filename}`);
  }
  return `/data/${filename}`;
};

// API base for backend (Express) if available
const API_BASE = 'https://backend-api.techkarmic.com';

const getApiUrl = (path: string, params?: Record<string, any>): string => {
  const base = API_BASE || '';
  const url = base ? joinUrl(base, path) : path;
  if (!params) return url;
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    usp.set(k, String(v));
  });
  const qs = usp.toString();
  return qs ? `${url}?${qs}` : url;
};

// Generic data loader with caching and error handling
async function loadData<T>(filename: string): Promise<T> {
  if (dataCache.has(filename)) {
    return dataCache.get(filename);
  }

  try {
    const response = await fetch(getDataUrl(filename));
    const contentType = response.headers.get('content-type') ?? '';
    
    if (!response.ok || !contentType.includes('application/json')) {
    throw new Error(
      `Failed to load ${filename}: ${response.status} — got ${contentType}, expected JSON`
    );
  }
    console.log({
      response
    })
    const data = await response.json();

    dataCache.set(filename, data);
    return data;
  } catch (error) {
    console.error(`Error loading ${filename}:`, error);
    throw new Error(`Failed to load data from ${filename}`);
  }
}

// Specific data loaders
export const loadIFSCCodes = (): Promise<IFSCCode[]> => 
  loadData<IFSCCode[]>('ifsc-codes.json');

export const loadPinCodes = (): Promise<PinCode[]> => 
  loadData<PinCode[]>('pin-codes.json');

export const loadCricketData = (): Promise<CricketData> => 
  loadData<CricketData>('cricket.json');

export const loadFestivals = (): Promise<Festival[]> => 
  loadData<Festival[]>('festivals.json');

export const loadRTOCodes = (): Promise<RTOCode[]> => 
  loadData<RTOCode[]>('rto-codes.json');

export const loadHTMLData = (): Promise<HTMLData> => 
  loadData<HTMLData>('html-colors.json');

export const loadRailwayStations = (): Promise<RailwayStation[]> => 
  loadData<RailwayStation[]>('railway-stations.json');

export const loadSTDCodes = (): Promise<STDCode[]> => 
  loadData<STDCode[]>('std-codes.json');

export const loadUSSDCodes = (): Promise<USSDCode[]> => 
  loadData<USSDCode[]>('ussd-codes.json');

type IFSCMap = Record<string, Array<number | string>>;

// List of full IFSC strings, for validation/autocomplete
export const loadIFSCList = (): Promise<string[]> =>
  loadData<string[]>('ifsc/IFSC-list.json');

// Compact map of bank prefixes to branch ids/special tokens
export const loadIFSCMap = (): Promise<IFSCMap> =>
  loadData<IFSCMap>('ifsc/IFSC.json');

// Bank directory/metadata (prefix -> info like micr, iin, type, etc.)
export const loadBankDirectory = (): Promise<Record<string, any>> =>
  loadData<Record<string, any>>('ifsc/banks.json');

// Detailed per-bank IFSC dataset; file is an object keyed by IFSC -> details
export const loadIFSCByBank = (bankPrefix: string): Promise<Record<string, any>> => {
  const file1 = `ifsc/by-bank/${bankPrefix}.json`;
  return loadData<Record<string, any>>(file1);
};

// ===================== IFSC API CLIENT HELPERS =====================
export type IFSCSearchParams = {
  q?: string;
  bank?: string;       // bank name
  bank_code?: string;  // prefix like SBIN
  branch?: string;
  city?: string;
  district?: string;
  state?: string;
  limit?: number;
  offset?: number;     // use offset for server pagination
};

type IFSCAPIItem = {
  ifsc: string;
  bank?: string;
  bank_code?: string;
  branch?: string;
  city?: string;
  district?: string;
  state?: string;
  address?: string;
};

export async function apiSearchIFSC(params: IFSCSearchParams): Promise<{ items: IFSCCode[]; total: number; }> {
  const url = getApiUrl('/data/ifsc/', params);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`API search failed: ${res.status}`);
  const data = await res.json();
  // Accept array or object { items, total }
  const itemsRaw: IFSCAPIItem[] = data.data.records;
  const total: number = data.data.total;
  const items: IFSCCode[] = itemsRaw.map(d => ({
    ifsc: d.ifsc,
    bank: d.bank ?? (d as any).BANK ?? '',
    branch: d.branch ?? (d as any).BRANCH ?? '',
    city: d.city ?? (d as any).CITY ?? (d as any).CENTRE ?? '',
    state: d.state ?? (d as any).STATE ?? '',
    address: (d as any).address ?? (d as any).ADDRESS ?? ''
  }));
  return { items, total };
}

export async function apiListBanks(): Promise<string[]> {
  const url = getApiUrl('/data/ifsc/banks');
  const res = await fetch(url);
  if (!res.ok) throw new Error(`API banks failed: ${res.status}`);
  const data = await res.json();
  return data.data;
}

// Return total count of distinct cities that match provided filters
export async function apiCountCities(params: IFSCSearchParams): Promise<number> {
  const url = getApiUrl('/data/ifsc/cities', params);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`API city count failed: ${res.status}`);
  const data = await res.json();
  const total = data.data.length;
  return total;
}

export async function apiGetByIFSC(ifsc: string): Promise<IFSCCode | null> {
  const url = getApiUrl(`/data/ifsc/${encodeURIComponent(ifsc)}`);
  const res = await fetch(url);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`API getByIFSC failed: ${res.status}`);
  const d = await res.json();
  console.log({
    d
  })
  return {
    ifsc: d.ifsc ?? d.IFSC,
    bank: d.bank ?? d.BANK ?? '',
    branch: d.branch ?? d.BRANCH ?? '',
    city: d.city ?? d.CITY ?? d.CENTRE ?? '',
    state: d.state ?? d.STATE ?? '',
    address: d.address ?? d.ADDRESS ?? ''
  };
}

// Search utilities for frontend filtering
export const searchData = <T extends Record<string, any>>(
  data: T[],
  query: string,
  searchFields: (keyof T)[]
): T[] => {
  if (!query.trim()) return data;
  
  const lowerQuery = query.toLowerCase();
  return data.filter(item =>
    searchFields.some(field => String(item[field] ?? '').toLowerCase().includes(lowerQuery))
  );
};

// Simple client-side pagination helper
export function paginateData<T>(data: T[], page: number, pageSize: number): { items: T[]; totalPages: number } {
  const totalPages = Math.max(1, Math.ceil((data?.length ?? 0) / pageSize));
  const current = Math.min(Math.max(1, page), totalPages);
  const start = (current - 1) * pageSize;
  const items = (data ?? []).slice(start, start + pageSize);
  return { items, totalPages };
}

// Pagination utility
export const paginateDataFull = <T>(
  data: T[],
  page: number,
  pageSize: number
): { items: T[]; totalPages: number; currentPage: number } => {
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  
  return {
    items: data.slice(startIndex, endIndex),
    totalPages: Math.ceil(data.length / pageSize),
    currentPage: page
  };
};

// Clear cache (useful for development)
export const clearDataCache = (): void => {
  dataCache.clear();
};
